import { ref, computed } from 'vue'
import { DEPARTMENT_TABS } from '../data/constants'

const users = ref([])
const isLoading = ref(false)
const isSaving = ref(false)
const loadError = ref(null)
const saveError = ref(null)
const saveSuccess = ref(null)

// All department options for the multi-select
const deptOptions = DEPARTMENT_TABS.map(d => ({ id: d.id, label: d.label, icon: d.icon }))

// Read response body as text first, then try to parse JSON. Avoids the
// cryptic "Failed to execute 'json' on 'Response': Unexpected end of JSON
// input" when the server (or an upstream proxy) sends an empty / non-JSON
// body. Returns { data, text } — `data` is null when body wasn't JSON.
async function readJsonResponse(resp) {
  const text = await resp.text()
  if (!text) return { data: null, text: '' }
  try {
    return { data: JSON.parse(text), text }
  } catch {
    return { data: null, text }
  }
}

function errorMessageFor(resp, data, text, fallback) {
  if (data && typeof data.message === 'string' && data.message) return data.message
  if (text && !data) {
    // Non-JSON body — surface a short snippet so devs can see what came back
    // (often HTML from a misrouted request, or plain text from a proxy error).
    const snippet = text.trim().slice(0, 200)
    return `HTTP ${resp.status} — ${snippet}`
  }
  if (!resp.ok) return `HTTP ${resp.status} (response kosong)`
  return fallback || `HTTP ${resp.status}`
}

async function fetchUsers() {
  isLoading.value = true
  loadError.value = null
  try {
    const resp = await fetch('/api/users')
    const { data, text } = await readJsonResponse(resp)
    if (!resp.ok || !data) throw new Error(errorMessageFor(resp, data, text, 'Gagal memuat user'))
    users.value = data.users || []
  } catch (err) {
    loadError.value = err.message
  } finally {
    isLoading.value = false
  }
}

async function createUser(userData) {
  isSaving.value = true
  saveError.value = null
  saveSuccess.value = null
  try {
    const resp = await fetch('/api/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    })
    const { data, text } = await readJsonResponse(resp)
    if (!resp.ok || !data) throw new Error(errorMessageFor(resp, data, text, 'Gagal membuat user'))
    users.value.push(data.user)
    saveSuccess.value = `User "${data.user.userName}" berhasil dibuat`
    return data.user
  } catch (err) {
    saveError.value = err.message
    return null
  } finally {
    isSaving.value = false
  }
}

async function updateUser(userId, userData) {
  isSaving.value = true
  saveError.value = null
  saveSuccess.value = null
  try {
    const resp = await fetch(`/api/users/${userId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    })
    const { data, text } = await readJsonResponse(resp)
    if (!resp.ok || !data) throw new Error(errorMessageFor(resp, data, text, 'Gagal mengupdate user'))
    const idx = users.value.findIndex(u => u.userId === userId)
    if (idx >= 0) {
      users.value[idx] = { ...users.value[idx], ...userData }
    }
    saveSuccess.value = 'User berhasil diupdate'
    return true
  } catch (err) {
    saveError.value = err.message
    return false
  } finally {
    isSaving.value = false
  }
}

async function deleteUser(userId) {
  isSaving.value = true
  saveError.value = null
  saveSuccess.value = null
  try {
    const resp = await fetch(`/api/users/${userId}`, { method: 'DELETE' })
    const { data, text } = await readJsonResponse(resp)
    if (!resp.ok || !data) throw new Error(errorMessageFor(resp, data, text, 'Gagal menghapus user'))
    users.value = users.value.filter(u => u.userId !== userId)
    saveSuccess.value = 'User berhasil dihapus'
    return true
  } catch (err) {
    saveError.value = err.message
    return false
  } finally {
    isSaving.value = false
  }
}

function clearMessages() {
  saveError.value = null
  saveSuccess.value = null
}

export function useUserManagement() {
  return {
    users,
    isLoading,
    isSaving,
    loadError,
    saveError,
    saveSuccess,
    deptOptions,
    fetchUsers,
    createUser,
    updateUser,
    deleteUser,
    clearMessages,
  }
}
